<template>
    <div class="log-manage wrapper" v-loading="loading">
        <proTitle></proTitle>

        <el-empty v-if="!loading && !enabledList.length" description="暂无已启用的日志，请先在「日志设置」中启用日志"></el-empty>

        <template v-else-if="enabledList.length">
            <!-- B2: 顶部日志卡片 -->
            <div class="log-cards-wrap" ref="logCardsWrap" @wheel.prevent="onCardsWheel">
                <div class="log-cards" ref="logCards" :style="logCardsStyle">
                    <div class="log-card" v-for="item in enabledList" :key="item.id"
                        :class="{ 'is-active': item.id === currentId }"
                        @click="selectTemplate(item.id)">
                        <div class="log-card-row1">
                            <span class="log-card-name" :title="(item.name || '').length > 6 ? item.name : ''">{{ truncName(item.name) }}</span>
                            <div class="log-card-export" @click.stop>
                                <div class="log-card-export-group"
                                    :class="{ 'is-loading': exportingId === item.id }">
                                    <i v-if="exportingId === item.id" class="el-icon-loading"></i>
                                    <template v-else>
                                        <button type="button" class="log-card-export-item"
                                            title="导出 Excel 表格"
                                            @click="exportLogs(item, 'xlsx')">
                                            <i class="el-icon-s-grid"></i><span>表格</span>
                                        </button>
                                        <button type="button" class="log-card-export-item"
                                            title="导出 Word 文档"
                                            @click="exportLogs(item, 'docx')">
                                            <i class="el-icon-document"></i><span>文档</span>
                                        </button>
                                        <button type="button" class="log-card-export-item"
                                            title="导出统计"
                                            @click="exportFillCount(item)">
                                            <i class="el-icon-s-data"></i><span>统计</span>
                                        </button>
                                    </template>
                                </div>
                            </div>
                        </div>
                        <div class="log-card-row2">{{ item.type === 2 ? '已确认' : '作答' }} {{ (item.dates || []).length }} 天</div>
                        <div class="log-card-row3">{{ formatGroupText(item) }}</div>
                        <div class="log-card-bottom">
                            <span class="log-card-date" :title="formatFullTime(item.updateAt)">{{ formatShortDate(item.updateAt) }}</span>
                            <span class="log-card-type-tag" :class="typeTagClass(item.type)">{{ typeLabel(item.type) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- B3: 日期条 -->
            <div class="date-bar-wrap" ref="dateBarWrap" @wheel.prevent="onDateBarWheel" v-if="openedDays.length">
                <div class="date-bar" ref="dateBar">
                    <div class="date-bar-item" v-for="day in openedDays" :key="day.id"
                        :class="{ 'is-active': day.id === currentDayId }"
                        @click="selectDay(day)">
                        <div class="date-bar-date">{{ formatDateLabel(day.logDate) }}</div>
                        <div class="date-bar-week">
                            <span>{{ weekdayLabel(day.logDate) }}</span>
                            <span class="date-bar-status" :class="dayStatusClass(day)">{{ dayStatusText(day) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- B4: 统计 -->
            <div class="stat-grid">
                <div class="stat-card">
                    <div class="stat-value">{{ overview ? overview.shouldSubmitCount : 0 }}</div>
                    <div class="stat-label">应提交人数</div>
                </div>
                <div class="stat-card">
                    <div class="stat-value is-green">{{ overview ? overview.submittedCount : 0 }}</div>
                    <div class="stat-label">已提交</div>
                </div>
                <div class="stat-card">
                    <div class="stat-value is-orange">{{ overview ? overview.unsubmittedCount : 0 }}</div>
                    <div class="stat-label">未提交</div>
                </div>
                <div class="stat-card">
                    <div class="stat-value">{{ overview ? formatRate(overview.submitRate) : '0.00%' }}</div>
                    <div class="stat-label">提交率</div>
                </div>
            </div>

            <!-- B4: 筛选 -->
            <div class="filter-seg">
                <button type="button" class="filter-seg-btn" :class="{ 'is-active': filter === 'all' }" @click="setFilter('all')">全部</button>
                <button type="button" class="filter-seg-btn" :class="{ 'is-active': filter === 'done' }" @click="setFilter('done')">已提交</button>
                <button type="button" class="filter-seg-btn" :class="{ 'is-active': filter === 'todo' }" @click="setFilter('todo')">未提交</button>
            </div>

            <!-- B5: 提交详情 / 提交用户 -->
            <div class="split">
                <div class="pane detail-pane">
                    <div class="pane-title">提交详情</div>
                    <div class="pane-content-area" v-if="selectedUser">
                        <div class="detail-meta">
                            <div class="avatar" :style="{ background: avatarColor(selectedUser) }">{{
                                (selectedUser.userName || '?').charAt(0) }}</div>
                            <div>
                                <div class="detail-title">{{ selectedUser.userName }}</div>
                                <div class="detail-sub">{{ userSub(selectedUser) }}</div>
                            </div>
                            <span class="status-capsule" :class="selectedUser._submitted ? 'is-submitted' : 'is-unsubmitted'">
                                {{ selectedUser._submitted ? '已提交' : '未提交' }}
                            </span>
                        </div>
                        <template v-if="selectedUser._submitted">
                            <div class="detail-text-scroll" :class="{ 'no-attach': !(selectedUser.files || []).length }">
                                <div class="detail-text">{{ selectedUser.content || '（无正文）' }}</div>
                            </div>
                            <div class="detail-attach-area" v-if="(selectedUser.files || []).length">
                                <div class="attach-label">提交附件（点击可预览）</div>
                                <div class="attach-list">
                                    <div class="attach-item" v-for="file in selectedUser.files" :key="file.id"
                                        @click="previewFile(file)">
                                        <div class="attach-thumb">
                                            <img v-if="fileCoverKind(file) === 'image'" :src="fileSrc(file)" alt="" />
                                            <template v-else-if="fileCoverKind(file) === 'video'">
                                                <video :src="videoThumb(fileSrc(file))" preload="metadata" muted class="attach-video-thumb"></video>
                                                <span class="attach-play-btn">▶</span>
                                            </template>
                                            <img v-else :src="fileIcon(file)" alt="" class="attach-type-icon" />
                                        </div>
                                        <div class="attach-name">{{ file.fileName }}</div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div v-else class="empty-block">该用户当日尚未提交日志</div>
                    </div>
                    <div class="empty-block" v-else>请从右侧选择用户查看提交详情</div>
                </div>
                <submit-user-pane
                    :users="filteredUsers"
                    :selected-key="selectedKey"
                    status-mode="submit"
                    @select="selectedKey = $event"
                />
            </div>
        </template>

        <AttachmentPreviewDialog
            :visible.sync="previewVisible"
            :file="previewFileObj"
        />
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { saveAs } from 'file-saver';
    import AttachmentPreviewDialog from '@/components/AttachmentPreviewDialog.vue';
    import SubmitUserPane from './components/submit-user-pane.vue';
    import {
        isApiSuccess,
        apiMsg,
        apiData,
        displayGroupNames,
        formatDateLabel,
        weekdayLabel,
        todayKey,
        parseDateTime,
        filenameFromDisposition,
        isImageExt,
        isVideoExt,
        isAllGroups,
        getAttachCoverKind,
        getAttachIcon,
        videoThumbSrc,
        resolveMediaUrl,
        overviewUserKey,
        overviewUserSub,
        overviewAvatarColor,
        buildOverviewUsers,
        filterOverviewUsers,
        pickDefaultOverviewUserKey,
        scrollDateChipToCenter,
    } from './log-moment-shared.js';

    export default {
        components: { AttachmentPreviewDialog, SubmitUserPane },
        data() {
            return {
                loading: false,
                list: [],
                currentId: null,
                currentDayId: null,
                overview: null,
                filter: 'all',
                selectedKey: null,
                exportingId: null,
                previewVisible: false,
                previewFileObj: null,
                today: todayKey(),
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            enabledList() {
                return (this.list || []).filter((item) => item.status === 1);
            },
            currentItem() {
                return this.enabledList.find((item) => item.id === this.currentId) || null;
            },
            currentDays() {
                return ((this.currentItem && this.currentItem.days) || []).slice().sort((a, b) => String(a.logDate).localeCompare(String(b.logDate)));
            },
            currentDay() {
                return this.currentDays.find((d) => d.id === this.currentDayId) || null;
            },
            openedDays() {
                return this.currentDays.filter((d) => this.isDayOpened(d));
            },
            allUsers() {
                return buildOverviewUsers(this.overview);
            },
            filteredUsers() {
                return filterOverviewUsers(this.allUsers, this.filter);
            },
            selectedUser() {
                return this.filteredUsers.find((u) => this.userKey(u) === this.selectedKey) || null;
            },
            logCardsStyle() {
                let count = this.enabledList.length;
                if (count <= 4) return {};
                return { width: (count * 25) + '%' };
            },
        },
        watch: {
            projectId: {
                immediate: true,
                handler(id) {
                    if (id) this.loadList();
                },
            },
            filter() {
                this.autoSelectFirstUser();
            },
            filteredUsers() {
                if (this.selectedKey && !this.filteredUsers.some((u) => this.userKey(u) === this.selectedKey)) {
                    this.autoSelectFirstUser();
                }
            },
        },
        methods: {
            displayGroupNames,
            formatDateLabel,
            weekdayLabel,
            typeLabel(type) {
                if (type === 2) return '个性日志';
                if (type === 3) return '通用日志（多题）';
                return '通用日志';
            },
            typeTagClass(type) {
                if (type === 2) return 'is-personal';
                if (type === 3) return 'is-multi';
                return 'is-common';
            },
            isImageExt,
            isVideoExt,
            fileCoverKind(file) {
                return getAttachCoverKind(file);
            },
            fileSrc(file) {
                return resolveMediaUrl(file, this.downloadURL);
            },
            fileIcon(file) {
                return getAttachIcon(getAttachCoverKind(file));
            },
            videoThumb(url) {
                return videoThumbSrc(url);
            },
            truncName(name) {
                if (!name) return '未命名';
                return name.length > 6 ? name.substring(0, 6) + '...' : name;
            },
            formatGroupText(item) {
                let names = displayGroupNames(item);
                if (!names || !names.length) return '未配置用户组';
                if (isAllGroups(item && item.userGroupIds, null, item)) return '适用全部学员';
                if (names.length === 1) return `适用${names[0]}`;
                if (names.length === 2) return `适用${names[0]}、${names[1]}`;
                return `适用${names[0]}等${names.length}个用户组`;
            },
            formatShortDate(val) {
                if (!val) return '-';
                let s = String(val);
                if (s.length >= 10) return s.substring(0, 10);
                return s;
            },
            formatFullTime(val) {
                if (!val) return '-';
                let s = String(val);
                if (s.length >= 16) return s.substring(0, 16);
                return s;
            },
            formatRate(val) {
                if (val == null || val === '') return '0.00%';
                return Number(val).toFixed(2) + '%';
            },
            userKey: overviewUserKey,
            userSub: overviewUserSub,
            avatarColor: overviewAvatarColor,
            isLogOpened(item) {
                if (!item) return true;
                let start = parseDateTime(item.submitStart);
                if (!start) return true;
                return new Date() >= start;
            },
            isDayOpened(day) {
                if (!this.currentItem || !day) return false;
                if (!this.isLogOpened(this.currentItem)) return false;
                return day.logDate <= this.today;
            },
            dayStatusText(day) {
                if (!this.isLogOpened(this.currentItem)) return '未开启';
                if (!this.isDayOpened(day)) return '未开启';
                if (day.logDate === this.today) return '今日';
                return '已开启';
            },
            dayStatusClass(day) {
                let text = this.dayStatusText(day);
                if (text === '今日') return 'is-today';
                if (text === '已开启') return 'is-opened';
                return 'is-unopened-tag';
            },
            defaultDayId() {
                let opened = this.openedDays;
                if (!opened.length) return null;
                let todayItem = opened.find((d) => d.logDate === this.today);
                if (todayItem) return todayItem.id;
                return opened[opened.length - 1].id;
            },
            autoSelectFirstUser() {
                this.selectedKey = pickDefaultOverviewUserKey(this.filteredUsers, this.filter);
            },
            setFilter(val) {
                this.filter = val;
            },
            onCardsWheel(e) {
                let wrap = this.$refs.logCardsWrap;
                if (wrap) wrap.scrollLeft += e.deltaY || e.deltaX;
            },
            onDateBarWheel(e) {
                let wrap = this.$refs.dateBarWrap;
                if (wrap) wrap.scrollLeft += e.deltaY || e.deltaX;
            },
            async loadList() {
                if (!this.projectId) return;
                this.loading = true;
                try {
                    let res = await this.$Api.Log.getTemplateList({ projectId: this.projectId });
                    if (!isApiSuccess(res)) {
                        this.$message.error(apiMsg(res) || '获取日志设置失败');
                        this.list = [];
                        return;
                    }
                    this.list = apiData(res) || [];
                    if (!this.enabledList.length) {
                        this.currentId = null;
                        this.overview = null;
                        return;
                    }
                    if (!this.enabledList.some((x) => x.id === this.currentId)) {
                        this.currentId = this.enabledList[0].id;
                    }
                    await this.ensureDayAndOverview();
                } finally {
                    this.loading = false;
                }
            },
            async selectTemplate(id) {
                this.currentId = id;
                this.filter = 'all';
                this.selectedKey = null;
                this.currentDayId = null;
                await this.ensureDayAndOverview();
            },
            async ensureDayAndOverview() {
                this.currentDayId = this.defaultDayId();
                if (this.currentDayId) await this.fetchOverview();
                else this.overview = null;
                this.scrollActiveDateChip();
            },
            async selectDay(day) {
                this.currentDayId = day.id;
                this.selectedKey = null;
                this.scrollActiveDateChip();
                await this.fetchOverview();
            },
            scrollActiveDateChip() {
                this.$nextTick(() => {
                    scrollDateChipToCenter(this.$refs.dateBarWrap, '.date-bar-item.is-active');
                });
            },
            async fetchOverview() {
                if (!this.currentDayId) return;
                this.loading = true;
                try {
                    let res = await this.$Api.Log.getDayOverview({ dayId: this.currentDayId });
                    if (!isApiSuccess(res)) {
                        this.$message.error(apiMsg(res) || '获取提交概况失败');
                        this.overview = null;
                        return;
                    }
                    this.overview = apiData(res) || {};
                    this.autoSelectFirstUser();
                } finally {
                    this.loading = false;
                }
            },
            async exportLogs(item, format) {
                if (!format) return;
                const isDocx = format === 'docx';
                this.exportingId = item.id;
                try {
                    const request = isDocx
                        ? this.$Api.Log.exportTemplateDocx
                        : this.$Api.Log.exportTemplate;
                    let res = await request({ templateId: item.id });
                    let blob = res.data;
                    if (blob && blob.type && blob.type.indexOf('application/json') >= 0) {
                        let msg = '导出失败';
                        try {
                            const text = await blob.text();
                            const json = JSON.parse(text);
                            if (json && json.msg) msg = json.msg;
                        } catch (e) { /* ignore */ }
                        this.$message.error(msg);
                        return;
                    }
                    const ext = isDocx ? 'zip' : 'xlsx';
                    let name = filenameFromDisposition(res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition']), `${item.name || '日志'}.${ext}`);
                    // 文档接口返回的是 zip，解压后才是 docx；响应头若仍带 .docx 也改成 .zip
                    if (isDocx) {
                        name = String(name || `${item.name || '日志'}.zip`).replace(/\.(docx|doc)$/i, '.zip');
                        if (!/\.zip$/i.test(name)) name += '.zip';
                    }
                    saveAs(blob, name);
                } catch (e) {
                    this.$message.error('导出失败');
                } finally {
                    this.exportingId = null;
                }
            },
            async exportFillCount(item) {
                if (!this.projectId) {
                    this.$message.error('未获取到项目');
                    return;
                }
                this.exportingId = item.id;
                try {
                    let res = await this.$Api.Log.exportUserFillCount({ projectId: this.projectId });
                    let blob = res && res.data;
                    if (!blob || (blob.type && blob.type.indexOf('application/json') >= 0)) {
                        let msg = '导出失败';
                        if (blob && typeof blob.text === 'function') {
                            try {
                                const json = JSON.parse(await blob.text());
                                if (json && json.msg) msg = json.msg;
                            } catch (e) { /* ignore */ }
                        }
                        this.$message.error(msg);
                        return;
                    }
                    let name = filenameFromDisposition(
                        res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition']),
                        '日志填写次数统计.xlsx'
                    );
                    saveAs(blob, name);
                } catch (e) {
                    this.$message.error('导出失败');
                } finally {
                    this.exportingId = null;
                }
            },
            previewFile(file) {
                let url = this.fileSrc(file);
                this.previewFileObj = {
                    ext: file.ext,
                    filename: file.fileName,
                    name: file.fileName,
                    url,
                    fileUrl: url,
                    ossUrl: url,
                };
                this.previewVisible = true;
            },
        },
    };
</script>

<style lang="less" scoped>
    @selected-blue: #3A7BD5;
    @selected-border: #8BB8F0;
    @border-color: #EBEEF5;
    @pane-h: 520px;
    @pane-title-h: 47px;
    @attach-row-h: 158px;

    .log-manage {
        padding: 20px 25px;
        box-sizing: border-box;
    }

    /* B2: 顶部日志卡片 */
    .log-cards-wrap {
        margin-bottom: 16px;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: thin;
        scrollbar-color: #D5D8DC transparent;

        &::-webkit-scrollbar {
            height: 4px;
        }

        &::-webkit-scrollbar-thumb {
            background: #D5D8DC;
            border-radius: 2px;
        }
    }

    .log-cards {
        display: flex;
        gap: 0;
        min-width: 100%;
    }

    .log-card {
        flex: 1 0 0;
        min-width: 0;
        background: #fff;
        border: 1px solid @border-color;
        border-radius: 8px;
        padding: 14px 16px;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        gap: 6px;
        position: relative;
        transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
        margin-right: 10px;

        &:last-child {
            margin-right: 0;
        }

        &:hover:not(.is-active) {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        &.is-active {
            border-color: @selected-border;
            border-left: 3px solid @selected-blue;
            box-shadow: 0 0 0 3px rgba(58, 123, 213, 0.1);

            .log-card-name {
                color: @selected-blue;
            }
        }
    }

    .log-card-row1 {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
    }

    .log-card-name {
        font-size: 14px;
        font-weight: 600;
        color: #1F2A37;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
        flex: 1;
    }

    .log-card-export {
        flex-shrink: 0;
    }

    .log-card-export-group {
        display: inline-flex;
        align-items: stretch;
        height: 26px;
        min-width: 138px;
        padding: 2px;
        box-sizing: border-box;
        background: #fff;
        border: 1px solid #E4E7ED;
        border-radius: 6px;
        box-shadow: 0 1px 2px rgba(31, 42, 55, 0.04);

        &.is-loading {
            justify-content: center;
            align-items: center;
            color: @selected-blue;
            font-size: 14px;
        }
    }

    .log-card.is-active .log-card-export-group {
        border-color: rgba(58, 123, 213, 0.28);
        background: #FAFCFF;
    }

    .log-card-export-item {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        margin: 0;
        padding: 0 8px;
        border: none;
        background: transparent;
        font-size: 11px;
        line-height: 1;
        color: #606266;
        cursor: pointer;
        border-radius: 4px;
        white-space: nowrap;
        transition: background 0.15s, color 0.15s, box-shadow 0.15s;

        i {
            font-size: 12px;
            color: #909399;
            transition: color 0.15s;
        }

        &:not(:last-child)::after {
            content: '';
            position: absolute;
            right: 0;
            top: 4px;
            bottom: 4px;
            width: 1px;
            background: #EBEEF5;
            pointer-events: none;
        }

        &:hover {
            background: @selected-blue;
            color: #fff;
            box-shadow: 0 1px 3px rgba(58, 123, 213, 0.35);

            i {
                color: #fff;
            }

            &::after {
                opacity: 0;
            }
        }

    }

    .log-card-row2,
    .log-card-row3 {
        font-size: 12px;
        color: #8B95A1;
        line-height: 18px;
    }

    .log-card-bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: auto;
    }

    .log-card-date {
        font-size: 12px;
        color: #B0B6BF;
    }

    .log-card-type-tag {
        display: inline-block;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        line-height: 18px;
        white-space: nowrap;

        &.is-common {
            background: #F3F8FF;
            color: @selected-blue;
        }

        &.is-personal {
            background: #FFF7E8;
            color: #C48A1A;
        }

        &.is-multi {
            background: #F3E8FF;
            color: #7C3AED;
        }
    }

    /* B3: 日期条 */
    .date-bar-wrap {
        margin-bottom: 16px;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: thin;
        scrollbar-color: #D5D8DC transparent;

        &::-webkit-scrollbar {
            height: 4px;
        }

        &::-webkit-scrollbar-thumb {
            background: #D5D8DC;
            border-radius: 2px;
        }
    }

    .date-bar {
        display: flex;
        min-width: 100%;
    }

    .date-bar-item {
        flex: 1 0 calc(100% / 7);
        min-width: calc(100% / 7);
        box-sizing: border-box;
        text-align: center;
        padding: 10px 8px;
        cursor: pointer;
        background: #fff;
        border: 1px solid @border-color;
        border-radius: 8px;
        margin-right: 8px;
        transition: border-color 0.15s, box-shadow 0.15s;

        &:last-child {
            margin-right: 0;
        }

        &:hover:not(.is-active) {
            border-color: #C0C4CC;
        }

        &.is-active {
            border-color: @selected-border;
            border-left: 3px solid @selected-blue;
            box-shadow: 0 0 0 3px rgba(58, 123, 213, 0.1);

            .date-bar-date {
                color: @selected-blue;
            }
        }

        &.is-unopened {
            .date-bar-date,
            .date-bar-week {
                color: #C0C4CC;
            }
        }
    }

    .date-bar-date {
        font-size: 13px;
        font-weight: 600;
        color: #1F2A37;
        line-height: 20px;
    }

    .date-bar-week {
        font-size: 12px;
        color: #8B95A1;
        margin-top: 2px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
    }

    .date-bar-status {
        display: inline-block;
        padding: 0 6px;
        border-radius: 8px;
        font-size: 11px;
        line-height: 18px;

        &.is-today {
            background: #F3F8FF;
            color: @selected-blue;
        }

        &.is-opened {
            background: #EEF8F0;
            color: #3D9A5A;
        }

        &.is-unopened-tag {
            background: #F4F6F8;
            color: #8B95A1;
        }
    }

    /* B4: 统计 */
    .stat-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 12px;
        margin-bottom: 16px;
    }

    .stat-card {
        background: #fff;
        border: 1px solid @border-color;
        border-radius: 8px;
        padding: 16px 20px;
        text-align: center;
    }

    .stat-value {
        font-size: 28px;
        font-weight: 700;
        color: #1F2A37;

        &.is-green {
            color: #3D9A5A;
        }

        &.is-orange {
            color: #E6A23C;
        }
    }

    .stat-label {
        font-size: 13px;
        color: #8B95A1;
        margin-top: 4px;
    }

    /* B4: 筛选 */
    .filter-seg {
        display: inline-flex;
        padding: 3px;
        background: #F0F2F5;
        border-radius: 8px;
        gap: 2px;
        margin-bottom: 16px;
    }

    .filter-seg-btn {
        padding: 6px 16px;
        border: none;
        border-radius: 6px;
        background: transparent;
        font-size: 13px;
        font-weight: 500;
        color: #606266;
        cursor: pointer;
        font-family: inherit;

        &.is-active {
            background: #fff;
            color: @selected-blue;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }
    }

    /* B5: 提交详情 / 提交用户 */
    .split {
        display: grid;
        grid-template-columns: 1fr 300px;
        gap: 16px;
    }

    .pane {
        background: #fff;
        border: 1px solid @border-color;
        border-radius: 8px;
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;
    }

    .pane-title {
        height: @pane-title-h;
        padding: 0 16px;
        font-size: 14px;
        font-weight: 600;
        border-bottom: 1px solid @border-color;
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
    }

    .pane-count {
        font-size: 12px;
        font-weight: 400;
        color: #8B95A1;
    }

    .detail-pane {
        .pane-content-area {
            display: flex;
            flex-direction: column;
            min-height: 0;
        }

        .detail-text-scroll {
            height: calc(@pane-h - @pane-title-h - @attach-row-h);
            overflow-y: auto;
            padding: 0 20px;

            &.no-attach {
                height: calc(@pane-h - @pane-title-h - 80px);
            }
        }

        .detail-attach-area {
            padding: 12px 20px;
            border-top: 1px solid @border-color;
        }
    }

    .user-pane {
        height: @pane-h;
    }

    .detail-meta {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px 20px;
        flex-shrink: 0;
    }

    .detail-title {
        font-size: 16px;
        font-weight: 600;
    }

    .detail-sub {
        font-size: 12px;
        color: #8B95A1;
        margin-top: 2px;
    }

    .detail-text {
        font-size: 14px;
        color: #606266;
        line-height: 1.8;
        white-space: pre-wrap;
    }

    .empty-block {
        min-height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #8B95A1;
        font-size: 13px;
        text-align: center;
        padding: 24px;

        &.sm {
            min-height: 80px;
        }
    }

    .attach-label {
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 10px;
    }

    .attach-list {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }

    .attach-item {
        width: 96px;
        border: 1px solid @border-color;
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        background: #F5F7FA;

        &:hover {
            border-color: @selected-blue;
        }
    }

    .attach-thumb {
        position: relative;
        width: 100%;
        height: 72px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #eef2f7;
        overflow: hidden;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    .attach-icon {
        font-size: 24px;
        color: #8B95A1;
    }

    .attach-video-thumb {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .attach-play-btn {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.5);
        color: #fff;
        font-size: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .attach-type-icon {
        width: 36px;
        height: 36px;
        object-fit: contain;
    }

    .attach-name {
        padding: 6px 8px;
        font-size: 11px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    /* 状态胶囊 */
    .status-capsule {
        display: inline-block;
        padding: 2px 10px;
        border-radius: 10px;
        font-size: 12px;
        line-height: 20px;
        margin-left: auto;
        white-space: nowrap;

        &.is-submitted {
            background: #EEF8F0;
            color: #3D9A5A;
        }

        &.is-unsubmitted {
            background: #F4F6F8;
            color: #8B95A1;
        }
    }

    .avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        font-weight: 600;
        color: #fff;
        flex-shrink: 0;

        &.sm {
            width: 32px;
            height: 32px;
            font-size: 13px;
        }
    }

    .user-list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding: 8px;
    }

    .user-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        border: 1px solid transparent;
        transition: background 0.15s, border-color 0.15s;

        &:hover {
            background: #F5F7FA;
        }

        &.is-active {
            background: #fff;
            border-color: @selected-border;
            border-left: 3px solid @selected-blue;
            box-shadow: 0 0 0 3px rgba(58, 123, 213, 0.1);
        }
    }

    .user-info {
        flex: 1;
        min-width: 0;
    }

    .user-name {
        font-size: 13px;
        font-weight: 600;
    }

    .user-sub {
        font-size: 12px;
        color: #8B95A1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    @media (max-width: 960px) {
        .split {
            grid-template-columns: 1fr;
        }

        .stat-grid {
            grid-template-columns: 1fr 1fr;
        }
    }
</style>
